// Pure, dependency-free. Duplicated verbatim to supabase/functions/_shared/entitlements.ts.
// KEEP THE TWO COPIES BYTE-IDENTICAL (sync test enforces this).

// Codes de forfait STABLES (base, Stripe lookup_keys, overrides) ; seuls libellés et limites
// changent avec la grille v1.6 : freemium = Free, lite = Lite, starter = Professional,
// core = Business, enterprise = Enterprise (libellés dans planPricing.ts).
export type Plan = 'freemium' | 'lite' | 'starter' | 'core' | 'enterprise';
export type QuotaMetric = 'jd' | 'search' | 'enrich' | 'outreach';
export type QuotaVal = number | 'unlimited';

export interface PlanDef {
  features: Record<string, boolean>;
  // Quotas PAR SIÈGE et par mois (mutualisés au niveau entreprise : limite effective =
  // valeur × sièges payés, voir #19). Règle (a) : on ne décompte que ce qui est AFFICHÉ.
  quotas: Record<QuotaMetric, QuotaVal>;
  // Plafond de sièges du forfait ; null = sans maximum.
  maxSeats: number | null;
}

const UNL = 'unlimited' as const;

// Feature groups (DRY): each tier layers onto the previous.
const FREEMIUM_FEATURES: Record<string, boolean> = {
  jd: true, pipeline: true, cv_library: true, candidate_manual: true,
  scoring: true, enrich: true, emily_ai: true, multichannel: true,
  search_advanced: true, geo_radius: true, jobboard_public: true,
  psychometric: false, multi_team: false, analytics: false, ats_merge: false,
  deel: false, generation: false, outplacement: false, support: false,
  vivier_pdl: false, byo_twilio: false, mcp: false,
  whatsapp: false, dedicated_wa_sender: false, emily_drafts_only: false,
  // #24 règle (b) : droit « téléphone » (click-to-call, numéros visibles) — deck slide 5 : Lite+.
  // Sans lui, aucun numéro ne quitte le serveur (fullenrich-proxy) et le front désactive tel:/wa.me.
  phone: false,
  wa_template_jd: true, // Template WhatsApp par JD : tous les forfaits (ordre Yarone 26/10)
};
const LITE_FEATURES     = { ...FREEMIUM_FEATURES, psychometric: true, emily_drafts_only: true, phone: true };
const STARTER_FEATURES  = { ...LITE_FEATURES, emily_drafts_only: false, multi_team: true, analytics: true, ats_merge: true, whatsapp: true };
const CORE_FEATURES     = { ...STARTER_FEATURES, generation: true, outplacement: true, support: true, vivier_pdl: true, deel: true, dedicated_wa_sender: true, wa_template_jd: true };
const ENT_FEATURES      = { ...CORE_FEATURES, byo_twilio: true, mcp: true };

// SOURCE UNIQUE côté TS de la grille v1.6 (deck slides 4-6). La copie SQL est semée par
// supabase/migrations/20261010090000_plan_limits_v16.sql (api.plan_limits) ; le test de dérive
// src/utils/planLimits.drift.test.ts impose l'égalité des deux.
export const PLAN_MATRIX: Record<Plan, PlanDef> = {
  freemium:   { features: FREEMIUM_FEATURES, quotas: { jd: 1,   search: 20,    enrich: 5,   outreach: 10  }, maxSeats: 1 },
  lite:       { features: LITE_FEATURES,     quotas: { jd: 3,   search: 5000,  enrich: 50,  outreach: 50  }, maxSeats: 1 },
  starter:    { features: STARTER_FEATURES,  quotas: { jd: UNL, search: 15000, enrich: 300, outreach: 150 }, maxSeats: 5 },
  core:       { features: CORE_FEATURES,     quotas: { jd: UNL, search: 25000, enrich: 500, outreach: 250 }, maxSeats: null },
  enterprise: { features: ENT_FEATURES,      quotas: { jd: UNL, search: UNL,   enrich: UNL, outreach: UNL }, maxSeats: null },
};

export interface ResolvedEntitlements {
  plan: Plan;
  features: Record<string, boolean>;
  quotas: Record<QuotaMetric, number>; // Infinity = unlimited
}

// Fail-closed (#13): an unknown or empty plan resolves to the MOST restrictive tier,
// never to 'enterprise'. Unknown non-empty values are logged (console.warn: browser
// console on the front, function logs on the edge) so legacy rows ('professional',
// 'startup', 'Enterprise ') surface instead of silently granting full access.
// Empty values (null / undefined / '') are the normal "not loaded yet" state: no log.
export function normalizePlan(plan: unknown): Plan {
  if (plan === 'freemium' || plan === 'lite' || plan === 'starter'
    || plan === 'core' || plan === 'enterprise') return plan;
  if (plan !== null && plan !== undefined && plan !== '') {
    console.warn('[entitlements] plan inconnu, repli fail-closed sur freemium :', String(plan));
  }
  return 'freemium';
}

// Override de quota VALIDE = 'unlimited' (chaîne exacte) ou nombre ENTIER ≥ 0 (#19, relecture 1/2).
// Même prédicat qu'isValidOverride (enforce-entitlement.ts) et qu'api.plan_limit_for_enterprise
// (SQL) : chaîne "40", -0.4, négatif, non entier, "Unlimited", booléen → ignoré → grille.
// Un non-entier accepté ici donnait un `remaining` fractionnaire transmis aux fournisseurs.
// Un entier au-delà du type integer Postgres est PLAFONNÉ (relecture 2/2), comme en SQL
// (least(v, 2147483647)) : même limite effective edge ↔ SQL, jamais ignoré, jamais « illimité ».
export const QUOTA_OVERRIDE_MAX = 2147483647;
function toNum(v: QuotaVal | undefined, fallback: number): number {
  if (v === UNL) return Infinity;
  if (typeof v === 'number' && Number.isInteger(v) && v >= 0) return Math.min(v, QUOTA_OVERRIDE_MAX);
  return fallback;
}

export function resolveEntitlements(plan: unknown, overrides: unknown): ResolvedEntitlements {
  const p = normalizePlan(plan);
  const base = PLAN_MATRIX[p];
  const ov = (overrides && typeof overrides === 'object') ? overrides as Record<string, unknown> : {};
  const ovFeatures = (ov.features && typeof ov.features === 'object') ? ov.features as Record<string, boolean> : {};
  const ovQuotas = (ov.quotas && typeof ov.quotas === 'object') ? ov.quotas as Record<string, QuotaVal> : {};

  const features: Record<string, boolean> = { ...base.features };
  for (const k of Object.keys(ovFeatures)) {
    if (typeof ovFeatures[k] === 'boolean') features[k] = ovFeatures[k];
  }

  const metrics: QuotaMetric[] = ['jd', 'search', 'enrich', 'outreach'];
  const quotas = {} as Record<QuotaMetric, number>;
  for (const m of metrics) {
    quotas[m] = toNum(ovQuotas[m] as QuotaVal | undefined, toNum(base.quotas[m], 0));
  }
  return { plan: p, features, quotas };
}

export function can(ent: ResolvedEntitlements, feature: string): boolean {
  return ent.features[feature] === true;
}

export function quota(ent: ResolvedEntitlements, metric: string): number {
  const v = ent.quotas[metric as QuotaMetric];
  return typeof v === 'number' ? v : 0;
}
